// Google Ads conversions in the app, under the cookie banner's one answer
// (#754, #755).
//
// Two conversions, each counted on a thank-you page of its own so Google Ads
// can count by URL as well as by event:
//
//   /welcome     a new account was made on the web: every door that makes
//                one (password, Google pop-up, Google redirect) lands here,
//                and goes on into the app.
//   /subscribed  Stripe Checkout came back complete, worth one month's price
//                (`PAID_VALUE`), with the Checkout session id as Google's
//                `transaction_id`. A card added in the trial is not charged
//                until it ends; the checkout is counted regardless.
//
// The shared parts — the tag, its configuration, the click ids, the cookies —
// are marketing/adTag.js, because the marketing site starts the same tag.
// This file is *when*, as src/lib/analytics.js is for Analytics, under that
// file's rules. It also hands each due conversion to src/lib/metaPixel.js
// (`metaConversionDue`, #780), which sends it as StartTrial or AddPaymentInfo
// under the same once-only rules, whether or not a Google Ads id is
// configured.
//
// ── On unless declined ─────────────────────────────────────────────────────
//
// gtag.js is fetched, and may set its `_gcl_*` cookies, while `getConsent()`
// says 'granted' — unless the visitor declined or the browser sends Global
// Privacy Control (marketing/consent.js). So a visitor who has not answered
// the notice is measured. While it says 'denied' nothing is fetched or set.
// With no Ads id configured (`VITE_GOOGLE_ADS_ID`, read in
// src/lib/firebase.js, never against the emulator) it is never fetched. A
// conversion whose label is not configured is not sent.
//
// A conversion is a property of the thank-you screen being shown, as a page
// view is: arriving on /welcome after a sign-up in this page, or on
// /subscribed with a Checkout session id, makes that screen's conversion due.
// It is sent at once if the tag is on, or when the visitor allows it while
// still on that screen, and dropped the moment they leave it or decline.
//
// ── Once ───────────────────────────────────────────────────────────────────
//
// The sign-up's "this page made an account" is held in memory only, so a
// refresh, a bookmark or the back button reaches /welcome without it and
// counts nothing. The Checkout session id is read from the address once and
// then taken out of it (useThankYouPages replaces the entry); a live session
// id seen in this page is never counted twice, and Google counts one
// conversion per `transaction_id`. A test-mode session (`cs_test_…`, a
// flagged test studio) is taken out of the address and counted nowhere.
//
// ── The click it is credited to ────────────────────────────────────────────
//
// The address this page loaded with may carry the ad's click id (the
// marketing site carries it onto its sign-up links, marketing/adMeasurement.js),
// and that is where gtag.js reads it. So the click ids are remembered for the
// page, put back on /welcome's address when the app sends a new account there,
// and added to the clean location the tag is given. Once running, the tag
// keeps the click in its `_gcl_aw` cookie on the brand's domain, which credits
// a subscription weeks later.
//
// ── What is sent ───────────────────────────────────────────────────────────
//
// One event, `conversion`, to `AW-…/<label>`: the page as its route pattern
// and the click ids, and for the paid one the value, the currency and the
// session id. No uid, email or studio id is passed. Enhanced conversions are on
// with Google's automatic management, so the tag itself may hash an email
// address it finds on the page and send that; marketing/adTag.js
// `adTagConfig` says where.
//
// ── A Decline turns it off in the same page ────────────────────────────────
//
// The tag is told the answer (all four signals denied, and redaction on), any
// due conversion is dropped, and the `_gcl_*` cookies and localStorage keys
// are removed (the GA ones are src/lib/analytics.js's), with no reload. A
// browser that starts denied and still holds them has them removed at startup.
// Nothing here throws into a caller.

import { getConsent, onConsentChange } from './consent';
import { loadCore } from './core.js';
import { pageViewParams } from './analyticsEvents';
import { BRAND } from '../domain/brand.js';
import { BILLING_CURRENCY, MONTHLY_PRICE_MINOR } from '../domain/billingPlans.js';
import {
  adTagConfig, attributionFrom, clearAdStorage, clickIdsOf, isAdsId, queryOf, startGoogleTag, updateGoogleTag,
} from '../../marketing/adTag.js';
import { metaConversionDue, metaNoteSignUp } from './metaPixel.js';

export const WELCOME_PATH = '/welcome';
export const SUBSCRIBED_PATH = '/subscribed';
/** The parameter STRIPE_CHECKOUT_SUCCESS_URL puts `{CHECKOUT_SESSION_ID}` in. */
export const CHECKOUT_SESSION_PARAM = 'session_id';

/** What a completed Checkout is worth to Google Ads: one month's price. */
export const PAID_VALUE = Object.freeze({ value: MONTHLY_PRICE_MINOR / 100, currency: BILLING_CURRENCY });

const LIVE_SESSION = /^cs_live_[A-Za-z0-9]{8,250}$/;
const LABEL = /^[A-Za-z0-9_-]{1,64}$/;

let wiring = null;
// 'off' | 'loading' | 'on'
let phase = 'off';
let gtag = null;
let clicks = [];
let currentPath = null;
// The conversion of the screen being shown: { kind, path, transactionId? }.
let due = null;
// 'none' | 'awaiting' (an account was made in this page, /welcome not yet
// reached) | 'welcomed'
let signUp = 'none';
let signUps = 0;
const seenSessions = new Set();
const signUpListeners = new Set();
let stopListening = null;

/**
 * Wire the Ads tag to the consent answer. Called once, from src/main.jsx.
 * `adsId` is the `AW-` id (none: nothing ever loads), the labels each
 * conversion action's; `search` the address's query at startup.
 * @returns {boolean} whether the tag is wired at all
 */
export function installAdConversions({ adsId, signUpLabel, paidLabel, search } = {}) {
  try {
    if (getConsent() !== 'granted') clearAdStorage();
  } catch {
    // No document: nothing to clear.
  }
  if (wiring || !isAdsId(adsId)) return false;
  const label = (l) => (typeof l === 'string' && LABEL.test(l) ? l : null);
  wiring = { id: adsId, labels: { signUp: label(signUpLabel), paid: label(paidLabel) } };
  clicks = clickIdsOf(attributionFrom(search ?? (typeof location === 'undefined' ? '' : location.search)));
  stopListening = onConsentChange((choice) => {
    if (choice === 'granted') start();
    else withdraw();
  });
  if (getConsent() === 'granted') start();
  return true;
}

// ── The sign-up's way to /welcome ──────────────────────────────────────────

/**
 * A new account was made in this page, by any door (AuthContext). The uid and
 * email are not kept here and never reach Google: they go to the Meta Pixel
 * (src/lib/metaPixel.js `metaNoteSignUp`), which keeps only their hashes.
 */
export function noteSignUp({ uid = null, email = null } = {}) {
  metaNoteSignUp({ uid, email });
  signUps += 1;
  signUp = 'awaiting';
  for (const fn of signUpListeners) fn();
}

/** Whether a new account made in this page has yet to reach /welcome. */
export const signUpAwaitingWelcome = () => signUp === 'awaiting';

/** How many accounts this page has made, so a caller can tell whether its own call made one. */
export const signUpsInThisPage = () => signUps;

/** Hear `noteSignUp` and the arrival on /welcome. Returns the unsubscribe. */
export function onSignUpChange(fn) {
  signUpListeners.add(fn);
  return () => signUpListeners.delete(fn);
}

/** Where a new account is sent: /welcome, with the ad's click ids when the tag is wired. */
export function welcomeTarget() {
  return { pathname: WELCOME_PATH, search: wiring ? queryOf(clicks) : '' };
}

// ── The screens ────────────────────────────────────────────────────────────

/**
 * The router's location, on every change (useThankYouPages). Makes a thank-you
 * screen's conversion due, drops one whose screen was left, and sends it if
 * the tag is on. Returns true when `search` carried a Checkout session id
 * that has now been read, so the caller can take it out of the address.
 */
export function reachScreen(pathname, search) {
  try {
    const path = String(pathname || '/').replace(/(.)\/+$/, '$1');
    currentPath = path;
    if (due && due.path !== path) due = null;
    if (path === WELCOME_PATH && signUp === 'awaiting') {
      signUp = 'welcomed';
      due = { kind: 'signUp', path };
      metaConversionDue({ kind: 'signUp', path });
      for (const fn of signUpListeners) fn();
    }
    let read = false;
    if (path === SUBSCRIBED_PATH) {
      const id = new URLSearchParams(String(search || '')).get(CHECKOUT_SESSION_PARAM);
      if (id != null) {
        read = true;
        if (LIVE_SESSION.test(id) && !seenSessions.has(id)) {
          seenSessions.add(id);
          due = { kind: 'paid', path, transactionId: id };
          metaConversionDue({ kind: 'paid', path, sessionId: id });
        }
      }
    }
    if (due && phase === 'on') sendDue();
    return read;
  } catch {
    return false;
  }
}

/** Forget everything, for tests. */
export function resetAdConversionsForTests() {
  stopListening?.();
  stopListening = null;
  wiring = null;
  phase = 'off';
  gtag = null;
  clicks = [];
  currentPath = null;
  due = null;
  signUp = 'none';
  signUps = 0;
  seenSessions.clear();
  signUpListeners.clear();
}

/** Where the module is, for tests. */
export const adConversionsPhase = () => phase;

function origin() {
  return typeof location === 'undefined' ? '' : location.origin;
}

// The clean location (route pattern, the core's rule) plus the click ids.
function adLocation(path) {
  return `${pageViewParams(path ?? '/', origin()).page_location}${queryOf(clicks)}`;
}

function sendDue() {
  const conversion = due;
  due = null;
  try {
    const label = wiring?.labels[conversion.kind];
    if (!label || !gtag) return false;
    const params = { send_to: `${wiring.id}/${label}`, page_location: adLocation(conversion.path) };
    if (conversion.kind === 'paid') {
      Object.assign(params, { ...PAID_VALUE, transaction_id: conversion.transactionId });
    }
    gtag('event', 'conversion', params);
    return true;
  } catch {
    return false;
  }
}

async function start() {
  if (!wiring || phase === 'loading') return;
  if (gtag) {
    // Allowed again after a Decline in this same page.
    try {
      updateGoogleTag(gtag, 'granted');
      phase = 'on';
      if (due) sendDue();
    } catch {
      phase = 'off';
    }
    return;
  }
  phase = 'loading';
  try {
    // The page patterns are the core's, and a grant read at startup can get
    // here before src/main.jsx's own wait for it is over.
    await loadCore();
    // Declined while it was loading: stop before anything is fetched.
    if (getConsent() !== 'granted') {
      phase = 'off';
      return;
    }
    gtag = startGoogleTag({
      id: wiring.id,
      config: adTagConfig({
        pageLocation: adLocation(currentPath),
        referrer: typeof document === 'undefined' ? '' : document.referrer,
        hostname: location.hostname,
        domain: BRAND.domain,
      }),
    });
    phase = 'on';
    if (due) sendDue();
  } catch {
    // Blocked, or no document: no Ads tag in this page.
    phase = 'off';
  }
}

function withdraw() {
  due = null;
  // A start in flight reads the answer again before it fetches anything.
  if (phase === 'on') phase = 'off';
  try {
    if (gtag) updateGoogleTag(gtag, 'denied');
  } catch {
    // The tag was never reachable.
  }
  try {
    clearAdStorage();
  } catch {
    // No document.
  }
}
